import { styled } from "@mui/material/styles";
import { ReactNode } from "react";
import ResizablePanel from "_shared/_views/resizeable-panel/resizable-panel";
import CollapsibleTemplatePanel from "work/proposals/_views/_shared/collapsible-template-panel";
import { paneIsCompact } from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";

const TemplatePanelContainer = styled(ResizablePanel, {
  shouldForwardProp: (prop) => prop !== "minimized",
})<{ minimized: boolean }>(({ theme, minimized }) => ({
  display: "flex",
  position: "relative",
  ...(minimized && {
    width: "auto !important",
    "& > div:last-of-type": {
      display: "none",
    },
  }),
  [paneIsCompact(theme)]: {
    flexShrink: 1,
    minWidth: 0,
    width: "100% !important",
    "& > div:first-of-type": {
      overflow: "visible",
    },
    "& > div:last-of-type": {
      display: "none",
    },
  },
}));

/** The narrowest the templates column goes, and — by `ResizablePanel`'s default — what it opens at. */
const TEMPLATE_PANEL_MIN_WIDTH = 375;

type ResizableTemplatePanelProps = {
  children: ReactNode;
  /** Lets a tab dress the column — an opacity for a waived collection, say — without restating its layout. */
  className?: string;
  minimized: boolean;
  selectedCount?: number;
};

/** A proposal template column with one consistent width, collapsed rail, and drag affordance. */
export default function ResizableTemplatePanel(props: Readonly<ResizableTemplatePanelProps>) {
  /**
   * The rail is what a *column* collapses to, so it is only ever offered while there is a column.
   *
   * Above `md` the panel sits beside the tab's content and a side panel opening takes the row's
   * width from it, which is what the rail trades away. A compact pane has no such column: the panel
   * is already stacked above the content as `TemplateSelector`'s own collapsed accordion — a bordered
   * summary you press to open — and there is nothing left for a rail to save. Asking for both left a
   * 44px vertical rail alone in a full-width band, with the accordion's line between the two sections
   * gone and the band's other 800px empty.
   */
  const paneIsCompact = useWorkPaneIsCompact();
  const minimized = props.minimized && !paneIsCompact;

  return (
    <TemplatePanelContainer
      className={props.className}
      minWidth={TEMPLATE_PANEL_MIN_WIDTH}
      minRemainingWidth={320}
      minimized={minimized}
    >
      <CollapsibleTemplatePanel minimized={minimized} selectedCount={props.selectedCount}>
        {props.children}
      </CollapsibleTemplatePanel>
    </TemplatePanelContainer>
  );
}
